import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Stethoscope, Microscope, Building2, Pill, Ambulance, ArrowRight } from 'lucide-react';
import { patientApi } from '../api';
import type { MarketplaceStats, MarketplaceProvider } from '../types';
import ProviderCard from '../components/ProviderCard';
import Spinner from '../../../components/ui/Spinner';

const CATEGORIES = [
  { type: 'DOCTOR' as const, label: 'Doctors', icon: Stethoscope, color: 'bg-blue-50 text-blue-600 hover:bg-blue-100', path: 'doctor', statsKey: 'doctors' as const },
  { type: 'LAB' as const, label: 'Labs', icon: Microscope, color: 'bg-purple-50 text-purple-600 hover:bg-purple-100', path: 'lab', statsKey: 'labs' as const },
  { type: 'HOSPITAL' as const, label: 'Hospitals', icon: Building2, color: 'bg-red-50 text-red-600 hover:bg-red-100', path: 'hospital', statsKey: 'hospitals' as const },
  { type: 'PHARMACY' as const, label: 'Pharmacies', icon: Pill, color: 'bg-green-50 text-green-600 hover:bg-green-100', path: 'pharmacy', statsKey: 'pharmacies' as const },
  { type: 'AMBULANCE' as const, label: 'Ambulances', icon: Ambulance, color: 'bg-orange-50 text-orange-600 hover:bg-orange-100', path: 'ambulance', statsKey: 'ambulances' as const },
];

export default function Marketplace() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<MarketplaceStats | null>(null);
  const [featured, setFeatured] = useState<MarketplaceProvider[]>([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<MarketplaceProvider[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([
      patientApi.getMarketplaceStats().then(data => { if (alive) setStats(data); }),
      patientApi.getMarketplaceProviders({ verified: 'true', limit: 6 }).then(r => { if (alive) setFeatured(r.providers); }),
    ]).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!search.trim()) { setSearchResults(null); return; }
    let alive = true;
    const timeout = setTimeout(() => {
      setSearching(true);
      patientApi.getMarketplaceProviders({ search: search.trim(), limit: 10 })
        .then(r => { if (alive) setSearchResults(r.providers); })
        .catch(() => { if (alive) setSearchResults([]); })
        .finally(() => { if (alive) setSearching(false); });
    }, 300);
    return () => { alive = false; clearTimeout(timeout); };
  }, [search]);

  if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Hero */}
      <div className="text-center">
        <h1 className="text-xl font-bold text-gray-900">Choose a verified provider</h1>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed px-4">Doctors, hospitals, pharmacies, labs, and ambulance services connected to your NeoHealthCard</p>
      </div>

      {/* Search */}
      <div className="relative max-w-lg mx-auto">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          className="input pl-10 !rounded-xl"
          placeholder="Search doctors, labs, hospitals..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {searching && <span className="absolute right-3 top-1/2 -translate-y-1/2"><Spinner size="sm" /></span>}
      </div>

      {/* Search Results */}
      {searchResults !== null && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{search}"</p>
          {searchResults.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No providers found matching your search</p>
          ) : (
            <div className="space-y-2">
              {searchResults.map(p => <ProviderCard key={p.id} provider={p} />)}
            </div>
          )}
        </div>
      )}

      {/* Categories */}
      {searchResults === null && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              const count = stats ? stats[cat.statsKey] : 0;
              return (
                <button
                  key={cat.type}
                  onClick={() => navigate(`/patient/marketplace/${cat.path}`)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-colors ${cat.color}`}
                >
                  <Icon className="w-7 h-7" />
                  <span className="text-sm font-semibold">{cat.label}</span>
                  <span className="text-xs opacity-70">{count} listed</span>
                </button>
              );
            })}
          </div>

          {/* Featured / Verified */}
          {featured.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-gray-900">Verified Providers</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {featured.map(p => <ProviderCard key={p.id} provider={p} />)}
              </div>
            </div>
          )}

          {/* Trust Note */}
          <div className="flex items-center justify-center gap-3 text-[10px] text-gray-400 py-2">
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-teal-400 inline-block" />Verified providers</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />Consent-based sharing</span>
          </div>
        </>
      )}
    </div>
  );
}
